import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Platform } from '@hangar/shared';
import { createEnvVar, createEnvVarVersion, createProfile, deleteEnvVar, getEnvRequirements, getLaunchForm, importEnvVars, listEnvVarVersions, listEnvVars, listProfiles, replaceEnvRequirements, resolveAppEnv, revealEnvVar, syncAppConfig, updateEnvVar, updateProfile, type EnvImportInput, type EnvResolveInput, type EnvVarCreateInput, type EnvVarFilters, type EnvVarPatchInput, type EnvVarVersionInput, type ProfilePatchInput, type ProfileWriteInput } from './endpoints';
import { type EnvRequirement } from './schemas';
import { qk } from './queryKeys';

/* ---------- Profiles ---------- */
export const useProfiles = (appId: string | undefined) =>
  useQuery({ queryKey: qk.profiles(appId ?? ''), queryFn: async () => (await listProfiles(appId ?? '')).data.filter((p) => !p.archivedAt), enabled: Boolean(appId) });

export const useLaunchForm = (profileId: string | undefined, environment: string | undefined, platform: Platform | 'all' | undefined, branch: string | undefined) =>
  useQuery({
    queryKey: qk.launchForm(profileId ?? '', environment ?? '', platform ?? '', branch ?? ''),
    queryFn: () => getLaunchForm(profileId ?? '', environment ?? '', platform, branch || undefined),
    enabled: Boolean(profileId && environment),
    staleTime: 15_000,
  });

export function useProfileMutations(appId: string) {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: qk.profiles(appId) });
    // Profiles live inside the app document: a profile write moves the app's lockVersion, which is the app's etag.
    void qc.invalidateQueries({ queryKey: ['apps'] });
  };
  const create = useMutation({ mutationFn: (v: { body: ProfileWriteInput; idempotencyKey: string }) => createProfile(appId, v.body, v.idempotencyKey), onSuccess: done });
  const update = useMutation({ mutationFn: (v: { profileId: string; body: ProfilePatchInput; etag?: string }) => updateProfile(v.profileId, v.body, v.etag), onSuccess: done });
  const sync = useMutation({
    mutationFn: (v: { content: string; dryRun: boolean; idempotencyKey?: string }) => syncAppConfig(appId, v.content, v.dryRun, v.idempotencyKey),
    onSuccess: (r) => {
      if (r.applied) {
        done();
        void qc.invalidateQueries({ queryKey: qk.envRequirements(appId) });
        void qc.invalidateQueries({ queryKey: ['apps'] });
      }
    },
  });
  return { create, update, sync };
}

/* ---------- Env vars ---------- */
export const useEnvVars = (filters: EnvVarFilters, enabled = true) =>
  useQuery({ queryKey: qk.envVars(filters), queryFn: async () => (await listEnvVars(filters)).data, enabled });
export const useEnvVarVersions = (id: string | undefined) =>
  useQuery({ queryKey: qk.envVarVersions(id ?? ''), queryFn: async () => (await listEnvVarVersions(id ?? '')).data, enabled: Boolean(id) });
export const useEnvRequirements = (appId: string | undefined) =>
  useQuery({ queryKey: qk.envRequirements(appId ?? ''), queryFn: () => getEnvRequirements(appId ?? ''), enabled: Boolean(appId) });

export function useEnvVarMutations() {
  const qc = useQueryClient();
  const done = (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['env-vars'] });
    if (id) void qc.invalidateQueries({ queryKey: qk.envVarVersions(id) });
  };
  const create = useMutation({ mutationFn: (v: { body: EnvVarCreateInput; idempotencyKey: string }) => createEnvVar(v.body, v.idempotencyKey), onSuccess: () => done() });
  const patch = useMutation({ mutationFn: (v: { id: string; body: EnvVarPatchInput; etag?: string }) => updateEnvVar(v.id, v.body, v.etag), onSuccess: (r) => done(r.id) });
  const remove = useMutation({ mutationFn: (v: { id: string; etag?: string }) => deleteEnvVar(v.id, v.etag), onSuccess: () => done() });
  const newVersion = useMutation({ mutationFn: (v: { id: string; body: EnvVarVersionInput; idempotencyKey: string }) => createEnvVarVersion(v.id, v.body, v.idempotencyKey), onSuccess: (r) => done(r.envVar.id) });
  const reveal = useMutation({ mutationFn: (v: { id: string; reason: string; versionId?: string }) => revealEnvVar(v.id, v.reason, v.versionId) });
  const importDotenv = useMutation({ mutationFn: (v: { body: EnvImportInput; idempotencyKey?: string }) => importEnvVars(v.body, v.idempotencyKey), onSuccess: (r) => (r.dryRun ? undefined : done()) });
  const resolve = useMutation({ mutationFn: (v: { appId: string; body: EnvResolveInput }) => resolveAppEnv(v.appId, v.body) });
  const requirements = useMutation({
    mutationFn: (v: { appId: string; requirements: EnvRequirement[]; etag?: string }) => replaceEnvRequirements(v.appId, v.requirements, v.etag),
    onSuccess: (r, v) => qc.setQueryData(qk.envRequirements(v.appId), r),
  });
  return { create, patch, remove, newVersion, reveal, importDotenv, resolve, requirements };
}
